import React, { useEffect, useRef, useState } from 'react';
import { useNavigate }                         from 'react-router-dom';
import { Html5Qrcode }                         from 'html5-qrcode';
import { useAuth }                             from '../contexts/AuthContext';
import { getVehicles, identifyPartPreview } from '../api/vehicles';
import { getUnknownPart, uploadImage, getCategories, createPart } from '../api/parts';
import './DashboardPage.css';

const ADMIN_NAV = { icon: '🛡', label: 'Admin', key: 'admin', route: '/admin' };

const NAV_ITEMS = [
  { icon: '🚗', label: 'My Projects', key: 'projects',  route: '/projects'  },
  { icon: '🔩', label: 'Parts',       key: 'parts',     route: '/parts'     },
  { icon: '📷', label: 'Images',      key: 'images',    route: '/vehicle-images' },
  { icon: '🔬', label: 'Research',    key: 'research',  route: '/research'  },
  { icon: '🛒', label: 'Cart',        key: 'cart',      route: '/cart'      },
  { icon: '🏪', label: 'Suppliers',   key: 'suppliers',  route: '/suppliers' },
  { icon: '📋', label: 'Reports',     key: 'reports'                        },
];

export default function DashboardPage() {
  const { user, signOut, isAdmin } = useAuth();
  const navigate                  = useNavigate();
  const [vehicle, setVehicle]     = useState(null);
  const [activeNav, setActiveNav] = useState('parts');
  const [navOpen, setNavOpen]     = useState(false);

  /* ── QR Scanner ────────────────────────────────────────────────── */
  const [qrOpen, setQrOpen]       = useState(false);
  const [qrError, setQrError]     = useState('');
  const [qrStatus, setQrStatus]   = useState('idle'); // idle | starting | scanning | success | error
  const qrRef                     = useRef(null);      // Html5Qrcode instance
  const qrDivRef                  = useRef(null);      // DOM container ref

  /* ── Identify Part ──────────────────────────────────────────────── */
  // step: 'pick' | 'analyzing' | 'result' | 'notfound' | 'saving'
  const [identOpen, setIdentOpen]         = useState(false);
  const [identStep, setIdentStep]         = useState('pick');
  const [identFile, setIdentFile]         = useState(null);
  const [identImageUrl, setIdentImageUrl] = useState('');
  const [identResult, setIdentResult]     = useState(null);   // full preview response
  const [identFields, setIdentFields]     = useState({});     // editable confirmation fields
  const [identBusy, setIdentBusy]         = useState(false);
  const [identError, setIdentError]       = useState('');
  const [identCategories, setIdentCategories] = useState([]);  // flat ref_category rows
  const [identCatId, setIdentCatId]           = useState(null); // selected top-level ID
  const [identSubCatId, setIdentSubCatId]     = useState(null); // selected sub-cat ID
  const fileInputRef                      = useRef(null);
  const cameraInputRef                    = useRef(null);
  const identDropRef                      = useRef(null);

  useEffect(() => {
    // 1. Try to use the vehicle that was selected on the Projects page
    const cached = localStorage.getItem('ar_active_vehicle');
    if (cached) {
      try { setVehicle(JSON.parse(cached)); } catch (_) {}
      return;
    }

    // 2. Fall back: fetch from API and use primary / first vehicle
    getVehicles()
      .then(res => {
        if (res.success && res.data.length) {
          const primary = res.data.find(v => v.is_primary === 1) || res.data[0];
          setVehicle(primary);
          localStorage.setItem('ar_active_vehicle', JSON.stringify(primary));
        } else {
          // No vehicles at all — send them to projects to create one
          navigate('/projects');
        }
      })
      .catch(() => {});
  }, [navigate]);

  /* ── QR helpers ────────────────────────────────────────────────── */
  function parsePartId(decodedText) {
    try {
      // Accept full URLs (any domain) or plain paths like /parts/123
      const url  = new URL(decodedText, window.location.origin);
      const m    = url.pathname.match(/\/parts\/(\d+)/);
      return m ? m[1] : null;
    } catch {
      const m = decodedText.match(/\/parts\/(\d+)/);
      return m ? m[1] : null;
    }
  }

  async function startQrScanner() {
    setQrError('');
    setQrStatus('starting');
    const containerId = 'qr-scanner-container';

    try {
      const html5Qr = new Html5Qrcode(containerId);
      qrRef.current = html5Qr;

      await html5Qr.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          // Success callback
          const partId = parsePartId(decodedText);
          if (partId) {
            setQrStatus('success');
            stopQrScanner();
            navigate(`/parts/${partId}`);
          } else {
            setQrError('QR code found but it is not a part link. Try again.');
          }
        },
        () => { /* error / no code yet — silent */ }
      );
      setQrStatus('scanning');
    } catch (err) {
      setQrStatus('error');
      if (err && err.message && err.message.toLowerCase().includes('permission')) {
        setQrError('Camera access denied. Please allow camera access and try again.');
      } else {
        setQrError('Could not start camera. ' + (err?.message || ''));
      }
    }
  }

  async function stopQrScanner() {
    try {
      if (qrRef.current) {
        await qrRef.current.stop();
        qrRef.current.clear();
        qrRef.current = null;
      }
    } catch (_) {}
  }

  function openQrModal() {
    setQrOpen(true);
    setQrError('');
    setQrStatus('idle');
  }

  function closeQrModal() {
    stopQrScanner();
    setQrOpen(false);
    setQrError('');
    setQrStatus('idle');
  }

  // Auto-start camera when modal opens
  useEffect(() => {
    if (qrOpen && qrStatus === 'idle') {
      // Small delay so DOM is ready
      const t = setTimeout(() => startQrScanner(), 200);
      return () => clearTimeout(t);
    }
    // Cleanup on close
    if (!qrOpen) {
      stopQrScanner();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrOpen]);

  /* ── Identify Part handlers ─────────────────────────────────────── */
  function openIdentModal() {
    setIdentOpen(true);
    setIdentStep('pick');
    setIdentFile(null);
    setIdentImageUrl('');
    setIdentResult(null);
    setIdentFields({});
    setIdentCategories([]);
    setIdentCatId(null);
    setIdentSubCatId(null);
    setIdentError('');
  }

  function closeIdentModal() {
    if (identBusy) return;
    if (identImageUrl) URL.revokeObjectURL(identImageUrl);
    setIdentOpen(false);
    setIdentStep('pick');
    setIdentFile(null);
    setIdentImageUrl('');
    setIdentResult(null);
    setIdentFields({});
    setIdentCategories([]);
    setIdentCatId(null);
    setIdentSubCatId(null);
    setIdentError('');
  }

  function applyIdentFile(file) {
    if (!file) return;
    if (identImageUrl) URL.revokeObjectURL(identImageUrl);
    setIdentFile(file);
    setIdentImageUrl(URL.createObjectURL(file));
    setIdentError('');
  }

  function handleIdentFileChange(e) {
    if (e.target.files?.[0]) applyIdentFile(e.target.files[0]);
    e.target.value = '';
  }

  function handleIdentDrop(e) {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) applyIdentFile(file);
  }

  function handleIdentFieldChange(key, value) {
    setIdentFields(f => ({ ...f, [key]: value }));
  }

  // Fuzzy-match an AI string to the closest category in a list
  function findCatMatch(list, aiName) {
    if (!aiName || !list?.length) return null;
    const needle = aiName.toLowerCase().trim();
    return (
      list.find(c => c.name.toLowerCase() === needle) ||
      list.find(c => needle.includes(c.name.toLowerCase()) ||
                     c.name.toLowerCase().includes(needle)) ||
      null
    );
  }

  async function handleAnalyze() {
    if (!identFile || !vehicle) return;
    setIdentStep('analyzing');
    setIdentError('');
    try {
      // Run AI preview and fetch category tree in parallel — saves ~300 ms
      const [previewRes, catRes] = await Promise.all([
        identifyPartPreview(vehicle.vehicleId, identFile),
        getCategories(vehicle.vehicleId)
      ]);

      if (!previewRes.success) throw new Error(previewRes.message || 'Identification failed');

      const { identification, existing_part } = previewRes.data;
      // getCategories returns { success, data: flatRows }
      const allCats = Array.isArray(catRes?.data) ? catRes.data
                    : Array.isArray(catRes)        ? catRes
                    : [];

      setIdentResult({ identification, existing_part });
      setIdentCategories(allCats);

      if (identification.identified) {
        // Pre-fill text fields from AI result (no category/sub_category — those use dropdowns)
        setIdentFields({
          name:              identification.name              || '',
          oem_part_number:   identification.oem_part_number  || '',
          assembly:          identification.assembly          || '',
          description:       identification.description       || '',
          restoration_notes: identification.restoration_notes || '',
          confidence:        identification.confidence        || ''
        });

        // Auto-select category dropdowns by fuzzy-matching AI strings to real DB names
        const topLevel = allCats.filter(c => !c.parentCategoryId);
        const matchedCat = findCatMatch(topLevel, identification.category);
        const matchedSubCat = matchedCat
          ? findCatMatch(
              allCats.filter(c => c.parentCategoryId === matchedCat.refCategoryId),
              identification.sub_category
            )
          : null;

        setIdentCatId(matchedCat?.refCategoryId ?? null);
        setIdentSubCatId(matchedSubCat?.refCategoryId ?? null);
        setIdentStep('result');
      } else {
        setIdentStep('notfound');
      }
    } catch (err) {
      setIdentError(err.message || 'AI identification failed. Please try again.');
      setIdentStep('pick');
    }
  }

  async function handleCreatePart() {
    if (!identFile || !vehicle) return;
    // Sub-category preferred; fall back to top-level if no sub-cat selected
    const refCategoryId = identSubCatId || identCatId;
    if (!refCategoryId) {
      setIdentError('Please select a category before creating the part.');
      return;
    }
    setIdentBusy(true);
    setIdentError('');
    try {
      // Use createPart (POST /parts → PART_manage UPSERT) with a real integer refCategoryId.
      // This bypasses PART_import's fuzzy-match entirely and avoids the TINYINT overflow.
      const createRes = await createPart({
        vehicleId:         vehicle.vehicleId,
        name:              (identFields.name || '').trim(),
        description:       identFields.description       || null,
        oem_part_number:   identFields.oem_part_number   || null,
        refCategoryId,
        refConditionId:    10,   // UNKNOWN condition
        refStatusId:       3,    // MISSING status
        restoration_notes: identFields.restoration_notes || null,
      });
      if (!createRes.success) throw new Error(createRes.message || 'Could not create part');
      const partId = createRes.data?.partId;
      if (!partId) throw new Error('Part created but ID not returned — please try again');

      // Upload the identified image to the new part
      await uploadImage(partId, identFile);

      closeIdentModal();
      navigate(`/parts/${partId}`);
    } catch (err) {
      setIdentError(err.message || 'Failed to create part. Please try again.');
      setIdentBusy(false);
    }
  }

  async function handleSaveToUnknown() {
    if (!identFile || !vehicle) return;
    setIdentBusy(true);
    setIdentError('');
    try {
      const upRes = await getUnknownPart(vehicle.vehicleId);
      if (!upRes.success) throw new Error('Could not find Unknown part for this vehicle');
      const unknownPartId = upRes.data.partId;
      await uploadImage(unknownPartId, identFile);
      closeIdentModal();
      navigate(`/parts/${unknownPartId}`);
    } catch (err) {
      setIdentError(err.message || 'Failed to save image. Please try again.');
      setIdentBusy(false);
    }
  }

  function confidenceBadge(confidence) {
    const map = { high: '#3a9a3a', medium: '#4d9de0', low: '#cc6600', confirmed: '#3a80aa', none: '#555' };
    const color = map[confidence] || map.none;
    return (
      <span className="ident-confidence-badge" style={{ background: color }}>
        {(confidence || 'unknown').toUpperCase()}
      </span>
    );
  }

  function handleSignOut() {
    signOut();
    localStorage.removeItem('ar_active_vehicle');
    navigate('/');
  }

  function handleNavClick(item) {
    setNavOpen(false);
    if (item.route) {
      navigate(item.route);
    } else {
      setActiveNav(item.key);
    }
  }

  function handleTileClick(item) {
    if (item.route) {
      navigate(item.route);
    } else {
      setActiveNav(item.key);
    }
  }

  const vehicleLabel = vehicle
    ? `${vehicle.year} ${vehicle.make} ${vehicle.model}${vehicle.trim_package ? ' ' + vehicle.trim_package : ''}`.trim()
    : '—';

  return (
    <div className="dash-root">

      {/* ── Top bar ─────────────────────────────────────────────── */}
      <header className="dash-header">
        <div className="dash-header-brand">
          <span className="dash-header-gear">⚙</span>
          <span className="dash-header-name">AutoRestoration</span>
        </div>

        <div className="dash-header-vehicle">
          {vehicle && (
            <>
              <span className="dash-vehicle-icon">🚗</span>
              <span className="dash-vehicle-label">{vehicleLabel}</span>
              {vehicle.nickname && (
                <span className="dash-vehicle-nick">"{vehicle.nickname}"</span>
              )}
            </>
          )}
        </div>

        <div className="dash-header-user">
          <span className="dash-username">@{user?.username}</span>
          <button className="dash-signout-btn" onClick={handleSignOut}>Sign Out</button>
          <button className="dash-nav-toggle" onClick={() => setNavOpen(o => !o)}
                  aria-label="Menu">☰</button>
        </div>
      </header>

      <div className="dash-body">

        {/* Mobile nav backdrop */}
        {navOpen && (
          <div
            style={{ position:'fixed', inset:0, zIndex:299, background:'rgba(0,0,0,0.5)' }}
            onClick={() => setNavOpen(false)}
          />
        )}

        {/* ── Side nav ──────────────────────────────────────────── */}
        <nav className={`dash-nav${navOpen ? ' open' : ''}`}>
          {NAV_ITEMS.map(item => (
            <button
              key={item.key}
              className={`dash-nav-item ${activeNav === item.key ? 'active' : ''} ${item.key === 'projects' ? 'projects-item' : ''}`}
              onClick={() => handleNavClick(item)}
            >
              <span className="dash-nav-icon">{item.icon}</span>
              <span className="dash-nav-label">{item.label}</span>
            </button>
          ))}

          {/* Admin nav — only visible to admin users */}
          {isAdmin && (
            <button
              className="dash-nav-item admin-item"
              onClick={() => { setNavOpen(false); navigate('/admin'); }}
            >
              <span className="dash-nav-icon">{ADMIN_NAV.icon}</span>
              <span className="dash-nav-label">{ADMIN_NAV.label}</span>
            </button>
          )}
        </nav>

        {/* ── Main content ──────────────────────────────────────── */}
        <main className="dash-main">

          {/* Vehicle hero banner */}
          {vehicle && (
            <div className="dash-vehicle-banner">
              <div className="dash-vehicle-banner-glow" />
              <div className="dash-vehicle-banner-content">
                <p className="dash-vehicle-banner-eyebrow">Active Project</p>
                <h2 className="dash-vehicle-banner-title">{vehicleLabel}</h2>
                {vehicle.nickname && (
                  <p className="dash-vehicle-banner-nick">"{vehicle.nickname}"</p>
                )}
                <div className="dash-vehicle-banner-meta">
                  {vehicle.engine_desc && (
                    <span>🔧 {vehicle.engine_desc}</span>
                  )}
                  {vehicle.transmission_desc && (
                    <span>⚙ {vehicle.transmission_desc}</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Welcome card */}
          <div className="dash-welcome">
            <h3 className="dash-welcome-heading">
              Welcome back, {user?.username}.
            </h3>
            <p className="dash-welcome-text">
              Your restoration project is ready. Use the navigation to manage
              parts, upload images, run AI identification, and access research.
            </p>
          </div>

          {/* Quick action tiles — exclude My Projects from the tile grid */}
          <div className="dash-tiles">
            {NAV_ITEMS.filter(i => i.key !== 'projects').map(item => (
              <button
                key={item.key}
                className="dash-tile"
                onClick={() => handleTileClick(item)}
              >
                <span className="dash-tile-icon">{item.icon}</span>
                <span className="dash-tile-label">{item.label}</span>
              </button>
            ))}

            {/* Scan QR Code tile */}
            <button className="dash-tile dash-tile-scan" onClick={openQrModal}>
              <span className="dash-tile-icon">📷</span>
              <span className="dash-tile-label">Scan QR</span>
            </button>

            {/* Identify Part tile */}
            <button className="dash-tile dash-tile-identify" onClick={openIdentModal}>
              <span className="dash-tile-icon">🔍</span>
              <span className="dash-tile-label">Identify Part</span>
            </button>
          </div>

          {/* Coming soon placeholder for active section */}
          <div className="dash-section-placeholder">
            <p className="dash-section-name">
              {NAV_ITEMS.find(i => i.key === activeNav)?.icon}{' '}
              {NAV_ITEMS.find(i => i.key === activeNav)?.label}
            </p>
            <p className="dash-section-msg">
              This section is coming soon.
            </p>
          </div>

        </main>
      </div>

      {/* ── Identify Part Modal ───────────────────────────────────── */}
      {identOpen && (
        <div className="ident-overlay" onClick={e => { if (e.target === e.currentTarget) closeIdentModal(); }}>
          <div className="ident-modal">

            {/* Header */}
            <div className="ident-header">
              <span className="ident-title">🔍 Identify Part</span>
              <button className="ident-close" onClick={closeIdentModal} disabled={identBusy}>✕</button>
            </div>

            {/* Hidden file inputs */}
            <input ref={fileInputRef}   type="file" accept="image/*"
                   style={{ display: 'none' }} onChange={handleIdentFileChange} />
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment"
                   style={{ display: 'none' }} onChange={handleIdentFileChange} />

            {/* ── STEP: pick ── */}
            {identStep === 'pick' && (
              <div className="ident-pick">
                {!identFile ? (
                  <>
                    <p className="ident-hint">
                      Upload or photograph a part and AI will identify it, suggest a category,
                      and populate as many fields as possible.
                    </p>
                    <div
                      className="ident-dropzone"
                      ref={identDropRef}
                      onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('drag-over'); }}
                      onDragLeave={e => e.currentTarget.classList.remove('drag-over')}
                      onDrop={e => { e.currentTarget.classList.remove('drag-over'); handleIdentDrop(e); }}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <span className="ident-dropzone-icon">🖼</span>
                      <span className="ident-dropzone-text">Drop image here or click to browse</span>
                    </div>
                    <div className="ident-or-row">
                      <span className="ident-or-line" />
                      <span className="ident-or-word">or</span>
                      <span className="ident-or-line" />
                    </div>
                    <button className="ident-camera-btn" onClick={() => cameraInputRef.current?.click()}>
                      📷 Take Photo
                    </button>
                  </>
                ) : (
                  <>
                    <div className="ident-preview-row">
                      <img className="ident-preview-thumb" src={identImageUrl} alt="Preview" />
                      <div className="ident-preview-info">
                        <span className="ident-preview-name">{identFile.name}</span>
                        <span className="ident-preview-size">
                          {(identFile.size / 1024).toFixed(0)} KB
                        </span>
                        <button className="ident-change-btn"
                                onClick={() => { setIdentFile(null); setIdentImageUrl(''); }}>
                          Change
                        </button>
                      </div>
                    </div>
                    {identError && <div className="ident-error">{identError}</div>}
                    <div className="ident-pick-actions">
                      <button className="ident-cancel-btn" onClick={closeIdentModal}>Cancel</button>
                      <button className="ident-analyze-btn" onClick={handleAnalyze}>
                        Analyze Part →
                      </button>
                    </div>
                  </>
                )}

                {identError && !identFile && <div className="ident-error">{identError}</div>}

                {!identFile && (
                  <button className="ident-cancel-btn" style={{ marginTop: '0.5rem' }}
                          onClick={closeIdentModal}>Cancel</button>
                )}
              </div>
            )}

            {/* ── STEP: analyzing ── */}
            {identStep === 'analyzing' && (
              <div className="ident-analyzing">
                <div className="ident-spin">⚙</div>
                <p className="ident-analyzing-text">Consulting AI…</p>
                <p className="ident-analyzing-sub">Analyzing image against vehicle specifications</p>
              </div>
            )}

            {/* ── STEP: result (identified) ── */}
            {identStep === 'result' && identResult && (
              <div className="ident-result">
                <div className="ident-result-header">
                  <div className="ident-result-thumb-wrap">
                    <img className="ident-result-thumb" src={identImageUrl} alt="Part" />
                  </div>
                  <div className="ident-result-meta">
                    <span className="ident-result-label">Part Identified</span>
                    {confidenceBadge(identResult.identification.confidence)}
                    {identResult.existing_part && (
                      <div className="ident-existing-notice">
                        <span className="ident-existing-icon">⚠</span>
                        <span>
                          Existing match:{' '}
                          <button className="ident-existing-link"
                                  onClick={() => { closeIdentModal(); navigate(`/parts/${identResult.existing_part.partId}`); }}>
                            {identResult.existing_part.name}
                          </button>
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="ident-fields">

                  {/* ── Text fields ── */}
                  {[
                    { key: 'name',            label: 'Part Name',      required: true },
                    { key: 'oem_part_number', label: 'OEM Part Number'                },
                  ].map(({ key, label, required }) => (
                    <div className="ident-field" key={key}>
                      <label className="ident-field-label">
                        {label}{required && <span className="ident-required">*</span>}
                      </label>
                      <input
                        className="ident-field-input"
                        value={identFields[key] || ''}
                        onChange={e => handleIdentFieldChange(key, e.target.value)}
                      />
                    </div>
                  ))}

                  {/* ── Cascading category dropdowns ── */}
                  {(() => {
                    const topLevel   = identCategories.filter(c => !c.parentCategoryId);
                    const subOptions = identCatId
                      ? identCategories.filter(c => c.parentCategoryId === identCatId)
                      : [];
                    return (
                      <>
                        <div className="ident-field">
                          <label className="ident-field-label">
                            Category<span className="ident-required">*</span>
                          </label>
                          <select
                            className="ident-field-input ident-field-select"
                            value={identCatId || ''}
                            onChange={e => {
                              setIdentCatId(parseInt(e.target.value) || null);
                              setIdentSubCatId(null); // reset sub when top changes
                            }}
                          >
                            <option value="">— Select Category —</option>
                            {topLevel.map(c => (
                              <option key={c.refCategoryId} value={c.refCategoryId}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="ident-field">
                          <label className="ident-field-label">Sub-Category</label>
                          <select
                            className="ident-field-input ident-field-select"
                            value={identSubCatId || ''}
                            onChange={e => setIdentSubCatId(parseInt(e.target.value) || null)}
                            disabled={!identCatId || subOptions.length === 0}
                          >
                            <option value="">
                              {!identCatId            ? '— Select a category first —'
                               : subOptions.length === 0 ? '— No sub-categories —'
                               : '— Select Sub-Category —'}
                            </option>
                            {subOptions.map(c => (
                              <option key={c.refCategoryId} value={c.refCategoryId}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </>
                    );
                  })()}

                  {/* ── Remaining text / textarea fields ── */}
                  {[
                    { key: 'assembly',          label: 'Assembly Group'                    },
                    { key: 'description',       label: 'Description',        textarea: true },
                    { key: 'restoration_notes', label: 'Restoration Notes',  textarea: true }
                  ].map(({ key, label, textarea }) => (
                    <div className="ident-field" key={key}>
                      <label className="ident-field-label">{label}</label>
                      {textarea ? (
                        <textarea
                          className="ident-field-input ident-field-textarea"
                          value={identFields[key] || ''}
                          onChange={e => handleIdentFieldChange(key, e.target.value)}
                          rows={2}
                        />
                      ) : (
                        <input
                          className="ident-field-input"
                          value={identFields[key] || ''}
                          onChange={e => handleIdentFieldChange(key, e.target.value)}
                        />
                      )}
                    </div>
                  ))}
                </div>

                {identError && <div className="ident-error">{identError}</div>}

                <div className="ident-result-actions">
                  <button className="ident-cancel-btn" onClick={closeIdentModal} disabled={identBusy}>
                    Cancel
                  </button>
                  <button
                    className="ident-create-btn"
                    onClick={handleCreatePart}
                    disabled={identBusy || !identFields.name?.trim() || !(identSubCatId || identCatId)}
                    title={!(identSubCatId || identCatId) ? 'Select a category first' : ''}
                  >
                    {identBusy ? 'Creating…' : '✓ Create Part'}
                  </button>
                </div>
              </div>
            )}

            {/* ── STEP: notfound ── */}
            {identStep === 'notfound' && identResult && (
              <div className="ident-notfound">
                <div className="ident-notfound-header">
                  <img className="ident-result-thumb" src={identImageUrl} alt="Part" />
                  <div>
                    <p className="ident-notfound-title">Could Not Identify</p>
                    <p className="ident-notfound-reason">
                      {identResult.identification.reason || 'AI could not determine the part from the provided image.'}
                    </p>
                  </div>
                </div>
                <p className="ident-notfound-hint">
                  The photo will be saved to your <strong>Unknown</strong> parts so you can assign it later.
                </p>
                {identError && <div className="ident-error">{identError}</div>}
                <div className="ident-result-actions">
                  <button className="ident-cancel-btn" onClick={closeIdentModal} disabled={identBusy}>
                    Cancel
                  </button>
                  <button className="ident-unknown-btn" onClick={handleSaveToUnknown} disabled={identBusy}>
                    {identBusy ? 'Saving…' : '📎 Save to Unknown'}
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      )}

      {/* ── QR Scanner Modal ──────────────────────────────────────── */}
      {qrOpen && (
        <div className="dash-qr-overlay" onClick={closeQrModal}>
          <div className="dash-qr-modal" onClick={e => e.stopPropagation()}>

            <div className="dash-qr-header">
              <span className="dash-qr-title">📷 Scan QR Code</span>
              <button className="dash-qr-close" onClick={closeQrModal}>✕</button>
            </div>

            <p className="dash-qr-hint">
              Point your camera at a part QR code to jump directly to that part.
            </p>

            {/* Camera viewport — html5-qrcode renders into this div */}
            <div className="dash-qr-viewport">
              <div id="qr-scanner-container" className="dash-qr-video" ref={qrDivRef} />

              {qrStatus === 'starting' && (
                <div className="dash-qr-starting">Starting camera…</div>
              )}
            </div>

            {qrError && (
              <div className="dash-qr-error">{qrError}</div>
            )}

            {qrStatus === 'error' && (
              <button className="dash-qr-retry" onClick={() => {
                setQrStatus('idle');
                setQrError('');
                startQrScanner();
              }}>
                Retry Camera
              </button>
            )}

            <button className="dash-qr-cancel" onClick={closeQrModal}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
